import type { PaginatedResponse } from "../api/zekay/pagination";

export const DEFAULT_PAGINATION_LIMIT = 10;

/**
 * Helper to extract data from paginated or non-paginated responses.
 * API returns snake_case: data, total, page, limit, total_pages.
 */
export function extractDataFromResponse<T>(result: PaginatedResponse<T> | T[] | undefined): T[] {
  if (!result) return [];
  if (Array.isArray(result)) return result;
  const r = result as unknown as { data?: T[] };
  if (Array.isArray(r.data)) return r.data;
  if (typeof result === "object" && result !== null && "Items" in result) {
    const items = (result as { Items: T[] }).Items;
    return Array.isArray(items) ? items : [];
  }
  return [];
}

/**
 * Helper to extract pagination metadata from paginated responses (snake_case).
 */
export function extractPaginationMetadata<T>(
  result: PaginatedResponse<T> | T[] | undefined,
): { total: number; page: number; limit: number; total_pages: number } | null {
  if (!result || typeof result !== "object") return null;
  const r = result as unknown as {
    data?: unknown;
    total?: number;
    page?: number;
    limit?: number;
    total_pages?: number;
  };
  if ("data" in r || "total" in r) {
    const total = Number(r.total ?? 0);
    const limit = Number(r.limit ?? 0);
    return {
      total,
      page: Number(r.page ?? 1),
      limit,
      total_pages: Number(r.total_pages ?? (limit > 0 ? Math.ceil(total / limit) : 0)),
    };
  }
  if ("Items" in result) {
    const ir = result as { Total?: number; Page?: number; Limit?: number };
    const total = Number(ir.Total ?? 0);
    const limit = Number(ir.Limit ?? 0);
    return {
      total,
      page: Number(ir.Page ?? 1),
      limit,
      total_pages: limit > 0 ? Math.ceil(total / limit) : 0,
    };
  }
  return null;
}

export function isPaginatedResponse<T>(
  result: PaginatedResponse<T> | T[] | undefined,
): result is PaginatedResponse<T> {
  if (result == null || typeof result !== "object") return false;
  const r = result as unknown as Record<string, unknown>;
  return "data" in r && r.total !== undefined;
}
